// Connectors in the sender's view (design §7): the record picker in the editor (search, or type a reference for
// Infor), and the card on the envelope page showing what each connector did, with Retry.
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Link2, Loader2, Plug, RefreshCw, Search, X } from "lucide-react";
import { api, useApi } from "../lib/api";
import { CONNECTOR_INFO, fullDate } from "../lib/format";
import type { Delivery, EnvelopeView, LinkableConnector, RecordLink, RecordSummary } from "../lib/types";
import { inputCls } from "./bits";
import { Button, cx, toast } from "./ui";

export const ConnectorDot = ({ type, className }: { type: string; className?: string }) => <span className={cx("inline-block size-2.5 rounded-full shrink-0", className)} style={{ background: CONNECTOR_INFO[type]?.color ?? "var(--muted)" }} aria-hidden />;

/** Editor: link a record from one of the company's CRM connectors; unlink. Hidden when none is set up. */
export function RecordPicker({ envelopeId, link, onChange, onBeforeChange }: { envelopeId: string; link?: RecordLink; onChange: (v: EnvelopeView) => void; onBeforeChange?: () => Promise<void> }) {
  const { data } = useApi<{ connectors: LinkableConnector[] }>("/connectors");
  const connectors = data?.connectors ?? [];
  const [cid, setCid] = useState<string>("");
  const conn = connectors.find((c) => c.id === cid) ?? connectors[0];
  const [q, setQ] = useState("");
  const [results, setResults] = useState<RecordSummary[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const text = q.trim();
    if (!conn || conn.picker !== "search" || text.length < 2) {
      setResults(null);
      return;
    }
    const n = ++seq.current;
    setSearching(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await api<{ records: RecordSummary[] }>(`/connectors/${conn.id}/records?q=${encodeURIComponent(text)}`);
        if (n === seq.current) setResults(r.records);
      } catch (e) {
        if (n === seq.current) {
          setResults([]);
          toast((e as Error).message, "error");
        }
      } finally {
        if (n === seq.current) setSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [q, conn]);

  const set = async (body: { connectorId: string | null; recordId?: string }) => {
    setBusy(true);
    try {
      await onBeforeChange?.();
      onChange(await api<EnvelopeView>(`/envelopes/${envelopeId}/link`, { body }));
      setQ("");
      setResults(null);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  if (!connectors.length && !link) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
        <Link2 className="size-4 text-muted" aria-hidden /> CRM record
      </h2>
      {link ? (
        <div className="rounded-lg border border-line p-2.5 text-sm">
          <div className="flex items-start gap-2">
            <ConnectorDot type={link.type} className="mt-1.5" />
            <div className="min-w-0 flex-1">
              {link.url ? (
                <a href={link.url} target="_blank" rel="noopener" className="font-medium text-ink hover:underline inline-flex items-center gap-1">
                  <span className="truncate">{link.recordName}</span> <ExternalLink className="size-3 shrink-0" />
                </a>
              ) : (
                <span className="font-medium text-ink truncate block">{link.recordName}</span>
              )}
              {link.detail && <div className="text-xs text-muted truncate">{link.detail}</div>}
            </div>
            <button className="p-1 rounded text-muted hover:bg-surface-2 disabled:opacity-40" disabled={busy} onClick={() => set({ connectorId: null })} aria-label="Unlink record" title="Unlink">
              <X className="size-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted mt-1.5">When everyone signs, the signed PDF goes to this record.</p>
        </div>
      ) : (
        <>
          {connectors.length > 1 && (
            <select value={conn?.id} onChange={(e) => (setCid(e.target.value), setQ(""))} aria-label="Connector" className={cx(inputCls, "h-8 mb-1.5")}>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          {conn?.picker === "manual" ? (
            <form
              className="flex gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (q.trim()) void set({ connectorId: conn.id, recordId: q.trim() });
              }}
            >
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${conn.label} record reference`} aria-label={`${conn.label} record reference`} className={cx(inputCls, "h-8")} />
              <Button type="submit" className="h-8" busy={busy} disabled={!q.trim()}>
                Link
              </Button>
            </form>
          ) : (
            conn && (
              <>
                <div className="relative">
                  <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${conn.name}, or paste a record link`} aria-label={`Find a ${conn.label} record`} className={cx(inputCls, "h-8 pl-8")} />
                  {(searching || busy) && <Loader2 className="size-4 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted" aria-hidden />}
                </div>
                {results && (
                  <ul className="mt-1.5 rounded-lg border border-line divide-y divide-line max-h-60 overflow-y-auto" role="listbox" aria-label="Records">
                    {results.length === 0 && !searching && <li className="px-2.5 py-2 text-xs text-muted">No matching records.</li>}
                    {results.map((o) => (
                      <li key={o.id}>
                        <button role="option" aria-selected={false} disabled={busy} onClick={() => set({ connectorId: conn.id, recordId: o.id })} className="w-full text-left px-2.5 py-2 hover:bg-surface-2">
                          <div className="text-sm font-medium text-ink truncate">{o.name}</div>
                          {o.detail && <div className="text-xs text-muted truncate">{o.detail}</div>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )
          )}
          <p className="text-xs text-muted mt-1.5">Optional. The signed PDF is filed on the record when everyone has signed.</p>
        </>
      )}
    </section>
  );
}

const STEP_LABEL: Record<string, string> = {
  posted: "Posted",
  file: "File added",
  field: "Field set",
  post: "Feed post",
  note: "Note added",
  property: "Property set",
  column: "Column set",
  pdf: "PDF written",
  audit: "Audit JSON written",
  idm: "Stored in IDM",
  landmark: "Landmark updated",
};

/** Envelope page: the linked record and what each connector did (or why it didn't), with Retry. */
export function DeliveriesCard({ envelopeId, status, link, deliveries, admin, reload }: { envelopeId: string; status: string; link?: RecordLink; deliveries: Delivery[]; admin?: boolean; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!link && !deliveries.length) return null;
  const retry = async (d: Delivery) => {
    setBusy(d.connectorId);
    try {
      await api(admin ? `/admin/envelopes/${envelopeId}/deliveries/${d.connectorId}/retry` : `/envelopes/${envelopeId}/deliveries/${d.connectorId}/retry`, { body: {} });
      toast(`Sending to ${d.name} again`);
      setTimeout(() => void reload(), 2500);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="bg-surface border border-line rounded-xl px-4 py-3 space-y-3">
      {link && (
        <div className="text-sm flex items-center gap-2">
          <ConnectorDot type={link.type} />
          <span className="text-muted">Linked record:</span>
          {link.url ? (
            <a href={link.url} target="_blank" rel="noopener" className="font-medium text-ink hover:underline inline-flex items-center gap-1 min-w-0">
              <span className="truncate">{link.recordName}</span> <ExternalLink className="size-3 shrink-0" />
            </a>
          ) : (
            <span className="font-medium truncate">{link.recordName}</span>
          )}
          {link.detail && <span className="text-muted truncate max-sm:hidden"> · {link.detail}</span>}
        </div>
      )}
      {link && !deliveries.length && <p className="text-xs text-muted flex items-center gap-1.5"><Plug className="size-3.5" /> The signed PDF is sent there when everyone has signed.</p>}
      {deliveries.map((d) => {
        const steps = Object.keys(d.steps).map((k) => STEP_LABEL[k] ?? k).join(", ");
        const line =
          d.status === "done"
            ? { icon: <CheckCircle2 className="size-4 text-good" />, text: `${d.name}: done ${fullDate(d.doneAt)}${steps ? ` (${steps})` : ""}`, tone: "text-good" }
            : d.status === "failed"
              ? { icon: <AlertTriangle className="size-4 text-bad" />, text: `${d.name}: ${d.lastError ?? "failed"}${d.attempts > 1 ? ` (tried ${d.attempts} times)` : ""}`, tone: "text-bad" }
              : d.status === "skipped"
                ? { icon: <Plug className="size-4 text-muted" />, text: `${d.name}: skipped (${d.lastError ?? "turned off"})`, tone: "text-muted" }
                : { icon: <Loader2 className="size-4 animate-spin text-muted" />, text: `Sending to ${d.name}…`, tone: "text-muted" };
        return (
          <div key={d.connectorId} className="flex items-start gap-3">
            <div className={cx("text-xs flex items-start gap-1.5 flex-1 min-w-0", line.tone)}>
              <span className="shrink-0 mt-px">{line.icon}</span>
              <span className="break-words">{line.text}</span>
            </div>
            {status !== "draft" && (d.status === "failed" || d.status === "pending") && (
              <Button className="h-7 text-xs shrink-0" busy={busy === d.connectorId} onClick={() => retry(d)}>
                <RefreshCw className="size-3.5" /> Retry
              </Button>
            )}
          </div>
        );
      })}
    </section>
  );
}
